import { useState } from "react";
import { useIntl } from "react-intl";
import { CaptionsOffIcon, CircleSlash2, MailPlus } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs/v2/TabsV2";
import classNames from "components/ui/classNames";
import StockSplitTabContent from "components/company/sections/content/StockSplitTabContent";
import RightsIssuesTabContent from "components/company/sections/content/RightsIssuesTabContent";
import SpinOffTabContent from "components/company/sections/content/SpinOffTabContent";
import FilterButtonTab from "components/company/sections/collapsible/utils/FilterButtonTab";

import type { IGlobalCorporateActionsQuery } from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import type {
  IRightsIssuesCorporateAction,
  ISpinOffCorporateAction,
  IStockSplitCorporateAction,
} from "types/graphqlTypes";

enum CorporateActionsTab {
  StockSplit = "STOCK_SPLIT",
  RightsIssues = "RIGHTS_ISSUES",
  SpinOff = "SPINOFF",
}

interface CorporateActionsContentProps {
  tickerSymbols: string[];
  data: IGlobalCorporateActionsQuery;
  isUSALocale: boolean;
  isLoading: boolean;
}

export default function CorporateActionsContent({
  tickerSymbols,
  data,
  isUSALocale,
  isLoading,
}: CorporateActionsContentProps) {
  const intl = useIntl();
  const [selectedTab, setSelectedTab] = useState<CorporateActionsTab>(CorporateActionsTab.StockSplit);
  const [filterByTickerSymbol, setFilterByTickerSymbol] = useState<string | null>(null);

  const hasStockSplit = data.stockSplit.length > 0;
  const hasRightsIssues = data.rightsIssues.length > 0;
  const hasSpinOff = data.spinOff.length > 0;

  const arrayOfTabs = [
    {
      value: CorporateActionsTab.StockSplit,
      title: intl.formatMessage({
        defaultMessage: "Eventos",
        id: "7A/Nj9",
        description: "Eventos Corporativos",
      }),
      icon: <CircleSlash2 size={16} strokeWidth={2} />,
      content: (
        <StockSplitTabContent
          data={data?.stockSplit.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node))}
          loading={isLoading}
          isUSALocale={isUSALocale}
        />
      ),
      isHidden: !hasStockSplit,
    },
    {
      value: CorporateActionsTab.RightsIssues,
      title: intl.formatMessage({
        defaultMessage: "Subscrição",
        id: "5WMOhr",
        description: "Eventos Corporativos",
      }),
      icon: <MailPlus size={16} strokeWidth={2} />,
      content: (
        <RightsIssuesTabContent
          data={data?.rightsIssues.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node))}
          loading={isLoading}
          isUSALocale={isUSALocale}
        />
      ),
      isHidden: !hasRightsIssues,
    },
    {
      value: CorporateActionsTab.SpinOff,
      title: intl.formatMessage({
        defaultMessage: "Cisão",
        id: "xDlFg4",
        description: "Eventos Corporativos",
      }),
      icon: <CaptionsOffIcon size={16} strokeWidth={2} />,
      content: (
        <SpinOffTabContent
          data={data?.spinOff.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node))}
          loading={isLoading}
          isUSALocale={isUSALocale}
        />
      ),
      isHidden: !hasSpinOff,
    },
  ];

  return (
    <div
      className={classNames(
        "relative rounded-xl border bg-white",
        "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
        "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
        "dark:border-transparent dark:bg-slate-900",
      )}
    >
      <Tabs
        className="w-full space-y-0 rounded-xl font-normal"
        value={selectedTab}
        defaultValue={selectedTab}
        onValueChange={(tab) => setSelectedTab(tab as CorporateActionsTab)}
      >
        <TabsList
          className={classNames(
            "flex w-full flex-1 divide-x overflow-hidden rounded-b-none rounded-t-xl p-0",
            "bg-slate-50",
            "dark:divide-slate-950 dark:border-slate-900 dark:bg-slate-800",
          )}
        >
          {arrayOfTabs
            .filter((tab) => !tab.isHidden)
            .map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={classNames(
                  "w-full justify-center overflow-hidden rounded-none border-b border-b-transparent px-2 md:justify-start",
                  "border-b-slate-200 dark:border-b-slate-950",
                  "group",
                )}
              >
                <div className="flex flex-col items-center gap-2 truncate py-1 text-left md:flex-row lg:px-1.5">
                  <div
                    className={classNames(
                      "flex items-center justify-center rounded-full p-2 squircle",
                      "bg-slate-50 dark:bg-indigo-300/10 dark:text-indigo-200",
                      "group-data-[state=active]:dark:bg-indigo-300/20 group-data-[state=active]:dark:text-indigo-100",
                      "group-data-[state=active]:bg-indigo-50/50 group-data-[state=active]:text-indigo-600",
                    )}
                  >
                    {tab.icon}
                  </div>
                  <span
                    className={classNames(
                      "text-xs leading-6 lg:text-base",
                      "text-slate-500 group-data-[state=active]:font-bold",
                      "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                    )}
                  >
                    {tab.title}
                  </span>
                </div>
              </TabsTrigger>
            ))}
        </TabsList>

        {!isUSALocale && tickerSymbols.length > 1 && (
          <div className="mb-2 grid w-full grid-cols-1 gap-x-3 sm:grid-cols-3">
            <FilterButtonTab
              labelText="Todas os Eventos"
              totalCount={getTotalCountByTypeTab(data, selectedTab, null)}
              isSelected={filterByTickerSymbol === null}
              onClick={() => {
                setFilterByTickerSymbol(null);
              }}
            />

            {tickerSymbols.map((tickerSymbol) => (
              <FilterButtonTab
                key={`FilterTab/${tickerSymbol}`}
                labelText={tickerSymbol}
                totalCount={getTotalCountByTypeTab(data, selectedTab, tickerSymbol)}
                isSelected={filterByTickerSymbol === tickerSymbol}
                onClick={() => {
                  setFilterByTickerSymbol(tickerSymbol);
                }}
              />
            ))}
          </div>
        )}

        {arrayOfTabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="border-none p-4 lg:px-6">
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function filterActionsByTickerSymbol(
  tickerSymbol: string | null,
  node: IStockSplitCorporateAction | IRightsIssuesCorporateAction | ISpinOffCorporateAction,
) {
  if (tickerSymbol && tickerSymbol === node.tickerSymbol) {
    return true;
  }

  if (!tickerSymbol) {
    return true;
  }

  return false;
}

function getTotalCountByTypeTab(
  data: IGlobalCorporateActionsQuery | undefined,
  selectedTab: CorporateActionsTab,
  tickerSymbol: string | null,
) {
  if (selectedTab === CorporateActionsTab.StockSplit) {
    return data?.stockSplit.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
  }

  if (selectedTab === CorporateActionsTab.RightsIssues) {
    return data?.rightsIssues.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
  }

  return data?.spinOff.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
}
